import type { WalkthroughStepType } from '@unifyapps/defs/types/page/walkthrough';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { useEffect, useState } from 'react';
import { useLatest } from 'react-use';
import { ARENA_SELECTOR } from '../../const';
import { useWalkthroughManagerStore } from '../../stores/WalkthroughManagerStore';
import { WalkthroughMode } from '../../stores/WalkthroughManagerStore/const';

/**
 * Hook that handles showAfter logic for walkthrough steps.
 * Manages delay and scroll-based step visibility.
 *
 * @param step - The walkthrough step to check visibility for
 * @returns Whether the step should be visible
 */
export function useWalkthroughStepVisibility(step: WalkthroughStepType | undefined): boolean {
  const [shouldShow, setShouldShow] = useState(false);
  const mode = useWalkthroughManagerStore().hooks.useMode();
  const containerEl = useContainerElementContext();
  const latestContainerEl = useLatest(containerEl);

  useEffect(() => {
    // Reset visibility when step changes
    setShouldShow(false);

    if (!step) {
      return;
    }

    // In builder/preview mode, show immediately (ignore showAfter)
    if (mode === WalkthroughMode.BUILDER || mode === WalkthroughMode.PREVIEW) {
      setShouldShow(true);
      return;
    }

    const { showAfter, showAfterDelay, showAfterScroll } = step;

    // If no showAfter condition, show immediately
    if (!showAfter) {
      setShouldShow(true);
      return;
    }

    if (showAfter === 'delay') {
      // Handle delay-based showing
      const delay = showAfterDelay ?? 0;
      if (delay <= 0) {
        setShouldShow(true);
        return;
      }

      const timeoutId = setTimeout(() => {
        setShouldShow(true);
      }, delay);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    if (showAfter === 'scroll') {
      // Handle scroll-based showing
      const targetScrollPercentage = showAfterScroll ?? 0;
      if (targetScrollPercentage <= 0) {
        setShouldShow(true);
        return;
      }

      // resolve scroll container: prefer explicit arena, fallback to container, then document's scrollingElement, finally window (SSR safe)
      const scrollContainer: HTMLElement | Window =
        (latestContainerEl.current?.closest?.(ARENA_SELECTOR) as HTMLElement) ||
        latestContainerEl.current ||
        (typeof document !== 'undefined' && (document.scrollingElement as HTMLElement | null)) ||
        (typeof window !== 'undefined' ? window : ({} as Window));

      const getScrollMetrics = () => {
        if (scrollContainer) {
          const scrollTop = scrollContainer.scrollTop;
          const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
          return { scrollTop, scrollHeight };
        }
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight =
          document.documentElement.scrollHeight - document.documentElement.clientHeight;
        return { scrollTop, scrollHeight };
      };

      const handleScroll = () => {
        const { scrollTop, scrollHeight } = getScrollMetrics();
        const currentScrollPercentage = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

        if (currentScrollPercentage >= targetScrollPercentage) {
          setShouldShow(true);
        }
      };

      // Check initial scroll position
      handleScroll();

      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }

    // Fallback: show immediately
    setShouldShow(true);
  }, [step, mode, step?.showAfter, step?.showAfterDelay, step?.showAfterScroll, latestContainerEl]);

  return shouldShow;
}
